import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "@/lib/api";
import { useAuthStore } from "@/stores/authStore";

export type MediaAsset = {
  id: string;
  brandId: string;
  source: "upload" | "whatsapp" | "generated" | "cart10" | "catalogue";
  originalUrl: string;
  enhancedUrl: string | null;
  prompt: string | null;
  createdAt: string;
};

/** `enhanceError` is set when the upload succeeded but enhancement did not. */
export type AssetResult = { asset: MediaAsset; enhanceError: string | null };

export function useAssets(brandId: string | null) {
  const workspaceId = useAuthStore((s) => s.workspaceId);
  return useQuery({
    queryKey: ["media", workspaceId, brandId],
    queryFn: () => unwrap<MediaAsset[]>(api.get("/media", { params: { brandId } })),
    enabled: !!workspaceId && !!brandId,
  });
}

/**
 * Uploads one photo as multipart.
 *
 * No Content-Type header is set on purpose: the browser must add the multipart
 * boundary itself, and setting the header by hand omits it, which makes the
 * server see a body it cannot parse.
 */
export function useUploadPhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { brandId: string; subject: string; enhance: boolean; file: File }) => {
      const form = new FormData();
      form.append("photo", args.file);
      form.append("brandId", args.brandId);
      form.append("subject", args.subject);
      form.append("enhance", String(args.enhance));
      return unwrap<AssetResult>(api.post("/media/upload", form));
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["media"] }),
  });
}

export function useEnhanceAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { assetId: string; subject: string }) =>
      unwrap<AssetResult>(api.post(`/media/${args.assetId}/enhance`, { subject: args.subject })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["media"] }),
  });
}

/** Back to the owner's own photo. */
export function useRevertAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (assetId: string) =>
      unwrap<MediaAsset>(api.post(`/media/${assetId}/revert`, {})),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["media"] }),
  });
}

export function usePostFromAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      assetId: string;
      topic?: string;
      facts?: string[];
      /** Empty = every connected page, matching the API default. */
      socialAccountIds?: string[];
    }) =>
      unwrap<{ imageError: string | null }>(
        api.post(`/media/${args.assetId}/post`, {
          ...(args.topic ? { topic: args.topic } : {}),
          ...(args.facts?.length ? { facts: args.facts } : {}),
          ...(args.socialAccountIds?.length
            ? { socialAccountIds: args.socialAccountIds }
            : {}),
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["calendar"] });
      void qc.invalidateQueries({ queryKey: ["media"] });
      // Completes the "first post" step.
      void qc.invalidateQueries({ queryKey: ["brands"] });
    },
  });
}
